import { Link } from "react-router-dom";
import { ArrowUpRight, Database, FileCheck2, Workflow, BarChart3, ShieldCheck, GitMerge, Bell, Clock } from "lucide-react";
import { Button } from "../components/ui/button";

const MODULES = [
  { icon: Database, title: "Claims Ingest", desc: "Pull EOBs and remits from your clearinghouse via SFTP, X12 835 parsing, or direct payer portal scraping." },
  { icon: FileCheck2, title: "Eligibility Engine", desc: "Auto-classifies each claim under NSA, state surprise law, ERISA, or out-of-scope — with the supporting CFR citations." },
  { icon: GitMerge, title: "QPA Validator", desc: "Compares the payer's QPA to FAIR Health, geographic medians, and historical contracted rates; flags low outliers for challenge." },
  { icon: Workflow, title: "Negotiation Workflow", desc: "Auto-drafts Open Negotiation letters, tracks the 30-day clock, and escalates to IDR initiation on day 31." },
  { icon: ShieldCheck, title: "IDRE Submission", desc: "Generates compliant IDR filings with credible information packets tuned to your service line and IDRE history." },
  { icon: BarChart3, title: "Recovery Analytics", desc: "Win rate, average uplift, payer-level performance, denial trends — exportable to your BI tool." },
  { icon: Bell, title: "Deadline Sentinel", desc: "Federal IDR deadlines are unforgiving. PRISM tracks every clock and routes overdue items before they expire." },
  { icon: Clock, title: "Audit Trail", desc: "Every action, document, and timestamp is preserved in an immutable, BAA-covered record." },
];

export default function Products() {
  return (
    <div data-testid="products-page">
      <section className="relative bg-white">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-16 sm:pt-28 sm:pb-20 relative">
          <div className="overline text-slate-500">Product</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight font-light mt-4 text-slate-900 max-w-4xl leading-[1.05]">
            The PRISM IDR Platform.
          </h1>
          <p className="mt-8 text-lg text-slate-600 max-w-2xl">
            A complete operating system for No Surprises Act dispute management. Eight integrated
            modules, one workspace, zero spreadsheets.
          </p>
          <Button
            asChild
            data-testid="products-cta-top"
            className="mt-10 rounded-md bg-[#0A2540] hover:bg-[#0F3A66] text-white h-12 px-6 gap-1"
          >
            <Link to="/contact">Book a demo <ArrowUpRight className="h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
            {MODULES.map((m, i) => (
              <div
                key={m.title}
                data-testid={`module-${i}`}
                className="bg-white p-8 group hover:bg-slate-50 transition-colors"
              >
                <div className="text-xs text-slate-400 font-mono">0{i + 1}</div>
                <m.icon className="h-7 w-7 text-[#0A2540] mt-4" />
                <div className="font-display text-lg font-medium mt-4 text-slate-900">{m.title}</div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A2540] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 text-center">
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-light">
            See PRISM resolve a live dispute.
          </h2>
          <p className="mt-6 text-slate-300 max-w-2xl mx-auto">
            45-minute working demo with our IDR strategists. Bring a real EOB and we'll walk
            through eligibility, QPA validation, and the recommended offer in front of you.
          </p>
          <Button
            asChild
            data-testid="products-cta-bottom"
            className="mt-10 rounded-md bg-white text-[#0A2540] hover:bg-blue-50 h-12 px-6"
          >
            <Link to="/contact">Schedule demo <ArrowUpRight className="h-4 w-4 ml-1" /></Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
